/**
 * validation — поэтапные user-friendly проверки. Sprint 2 P0.
 * Возвращает первую найденную ошибку (или null).
 *
 * Email rules: наличие @, local-part, домена, точки, TLD ≥2, валидные символы.
 * Password rules: 10+ символов, буквы и цифры (минимум).
 */

export function validateEmail(value: string): string | null {
  const v = value.trim()
  if (!v) return 'Введи email'
  if (v.length > 254) return 'Адрес слишком длинный (макс. 254 символа)'
  const atCount = (v.match(/@/g) || []).length
  if (atCount === 0) return `Адрес должен содержать символ «@». В адресе «${v}» символ «@» отсутствует.`
  if (atCount > 1) return `В адресе должен быть только один символ «@». Сейчас их ${atCount}.`
  const [local, domain] = v.split('@')
  if (!local) return 'До символа «@» должно быть имя. Введи что-то перед «@».'
  if (/\s/.test(local)) return 'Имя до «@» не должно содержать пробелы.'
  if (!/^[A-Za-z0-9._%+\-]+$/.test(local))
    return 'До «@» допускаются только латиница, цифры и `. _ % + -`. Кириллица не поддерживается почтовыми серверами.'
  if (!domain) return `После «@» должен быть домен. В адресе «${v}» домен отсутствует.`
  if (!domain.includes('.'))
    return `Домен должен содержать точку. В адресе «${v}» не указан регион (.ru, .com и т.п.).`
  const tld = domain.split('.').pop() || ''
  if (tld.length < 2) return 'Зона домена должна быть от 2 символов (например `.ru`, `.com`).'
  if (tld.length > 24) return 'Зона домена слишком длинная — проверь, нет ли опечатки.'
  if (!/^[A-Za-z]+$/.test(tld)) return 'Зона домена должна состоять только из букв.'
  if (!/^[A-Za-z0-9.\-]+$/.test(domain))
    return 'Домен должен быть на латинице. Кириллические домены (.рф) почтовые сервера не поддерживают.'
  return null
}

export interface PasswordCheck {
  label: string
  passed: boolean
}

export interface EmailCheck {
  label: string
  passed: boolean
}

export function emailChecks(value: string): EmailCheck[] {
  const v = value.trim()
  const atCount = (v.match(/@/g) || []).length
  const [local = '', domain = ''] = v.split('@')
  const domainHasDot = domain.includes('.')
  const tld = domainHasDot ? domain.split('.').pop() ?? '' : ''
  // Имя домена до последней точки (для проверки «домен после @»: должно быть
  // непустое имя ДО точки, а не сама точка).
  const domainName = domainHasDot ? domain.slice(0, domain.lastIndexOf('.')) : domain
  return [
    { label: 'Латиница и цифры до @', passed: !!local && /^[A-Za-z0-9._%+\-]+$/.test(local) },
    { label: 'Символ «@»', passed: atCount === 1 },
    { label: 'Домен после @ (латиница)', passed: atCount === 1 && !!domainName && /^[A-Za-z0-9\-]+(?:\.[A-Za-z0-9\-]+)*$/.test(domainName) },
    { label: 'Точка (.) после домена', passed: atCount === 1 && domainHasDot && !!domainName },
    { label: 'Регион (ru, com и т.п.)', passed: atCount === 1 && tld.length >= 2 && /^[A-Za-z]+$/.test(tld) },
  ]
}

export function validatePassword(value: string): { error: string | null; checks: PasswordCheck[] } {
  const checks: PasswordCheck[] = [
    { label: 'Минимум 10 символов', passed: value.length >= 10 },
    { label: 'Буквы и цифры', passed: /[A-Za-zА-Яа-яЁё]/.test(value) && /\d/.test(value) },
    {
      label: 'Большие и маленькие буквы',
      passed: /[a-zа-яё]/.test(value) && /[A-ZА-ЯЁ]/.test(value),
    },
    {
      label: 'Спецсимвол',
      passed: /[^A-Za-zА-Яа-яЁё\d]/.test(value),
    },
  ]
  if (!value) return { error: 'Введи пароль', checks }
  // Submit-уровень = «Сильный» = все 4 проверки. Длина 14+ — только бонус
  // для уровня «Очень сильный» (не обязательно).
  const failed = checks.find((c) => !c.passed)
  if (failed) return { error: `Не выполнено: ${failed.label.toLowerCase()}.`, checks }
  return { error: null, checks }
}
